import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "canvas",
    "signatureData",
    "pad",
    "hiddenField",
    "submitButton",
    "form",
    "existingDisplay",
    "existingImage",
    "padSection"
  ]

  connect() {
    if (this.hasPadTarget || this.hasCanvasTarget) {
      this.initializeCanvas()
    }

    if (document.getElementById("admin-signature-form")) {
      window.submitAdminSignature = () => {
        this.ensureCanvasReady()
        this.updateSignatureData()
        const form = document.getElementById("admin-signature-form")
        if (form) {
          form.submit()
        }
      }
    }
  }

  initializeCanvas() {
    if (this.canvas && this.ctx) return

    this.canvas = this.hasPadTarget
      ? this.padTarget
      : this.hasCanvasTarget
        ? this.canvasTarget
        : null

    if (!this.canvas) return

    this.ctx = this.canvas.getContext("2d")
    this.isDrawing = false
    this.hasDrawn = false
    this.lastX = 0
    this.lastY = 0

    this.setupCanvas()
    this.bindEvents()
    this.loadExistingSignature()
    this.updateSubmitButton()
    this.setupFormValidation()
  }

  ensureCanvasReady() {
    if (!this.canvas || !this.ctx) {
      this.initializeCanvas()
    }
  }

  setupFormValidation() {
    const form = this.canvas?.closest("form") || this.formTarget
    if (form) {
      form.addEventListener("submit", (event) => {
        if (this.isEmpty()) {
          event.preventDefault()
          alert(
            "⚠️ Please draw your signature on the canvas before submitting.\n\nYour signature is required to complete this form."
          )
          this.canvas?.scrollIntoView({ behavior: "smooth", block: "center" })
          return false
        }
      })
    }
  }

  setupCanvas() {
    this.ctx.strokeStyle = "#000000"
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"

    this.ctx.fillStyle = "#ffffff"
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
  }

  loadExistingSignature() {
    if (this.hasExistingDisplayTarget && !this.existingDisplayTarget.classList.contains("hidden")) {
      return
    }

    let existingSignature = null

    if (this.hasSignatureDataTarget && this.signatureDataTarget.value) {
      existingSignature = this.signatureDataTarget.value
    } else if (this.hasHiddenFieldTarget && this.hiddenFieldTarget.value) {
      existingSignature = this.hiddenFieldTarget.value
    }

    if (existingSignature && existingSignature.startsWith("data:image")) {
      const img = new Image()
      img.onload = () => {
        this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height)
        this.hasDrawn = true
        this.updateSubmitButton()
      }
      img.onerror = (error) => {
        console.error("Failed to load existing signature:", error)
      }
      img.src = existingSignature
    }
  }

  bindEvents() {
    this.canvas.addEventListener("mousedown", this.startDrawing.bind(this))
    this.canvas.addEventListener("mousemove", this.draw.bind(this))
    this.canvas.addEventListener("mouseup", this.stopDrawing.bind(this))
    this.canvas.addEventListener("mouseout", this.stopDrawing.bind(this))

    this.canvas.addEventListener("touchstart", this.handleTouch.bind(this))
    this.canvas.addEventListener("touchmove", this.handleTouch.bind(this))
    this.canvas.addEventListener("touchend", this.stopDrawing.bind(this))
  }

  startDrawing(event) {
    this.isDrawing = true
    const rect = this.canvas.getBoundingClientRect()
    const scaleX = this.canvas.width / rect.width
    const scaleY = this.canvas.height / rect.height

    this.lastX = (event.clientX - rect.left) * scaleX
    this.lastY = (event.clientY - rect.top) * scaleY
  }

  draw(event) {
    if (!this.isDrawing) return

    const rect = this.canvas.getBoundingClientRect()
    const scaleX = this.canvas.width / rect.width
    const scaleY = this.canvas.height / rect.height

    const currentX = (event.clientX - rect.left) * scaleX
    const currentY = (event.clientY - rect.top) * scaleY

    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(currentX, currentY)
    this.ctx.stroke()

    this.hasDrawn = true

    this.lastX = currentX
    this.lastY = currentY
  }

  stopDrawing() {
    if (!this.isDrawing) return

    this.isDrawing = false

    if (this.hasDrawn) {
      this.updateSignatureData()
      this.updateSubmitButton()
    }
  }

  handleTouch(event) {
    event.preventDefault()
    const touch = event.touches[0]
    const rect = this.canvas.getBoundingClientRect()
    const scaleX = this.canvas.width / rect.width
    const scaleY = this.canvas.height / rect.height

    if (event.type === "touchstart") {
      this.isDrawing = true
      this.lastX = (touch.clientX - rect.left) * scaleX
      this.lastY = (touch.clientY - rect.top) * scaleY
    } else if (event.type === "touchmove") {
      if (!this.isDrawing) return

      const currentX = (touch.clientX - rect.left) * scaleX
      const currentY = (touch.clientY - rect.top) * scaleY

      this.ctx.beginPath()
      this.ctx.moveTo(this.lastX, this.lastY)
      this.ctx.lineTo(currentX, currentY)
      this.ctx.stroke()

      this.hasDrawn = true

      this.lastX = currentX
      this.lastY = currentY
    }
  }

  clear() {
    this.ensureCanvasReady()
    if (!this.ctx || !this.canvas) return

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    this.ctx.fillStyle = "#ffffff"
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)

    this.hasDrawn = false

    this.clearStoredSignatureData()
    this.updateSubmitButton()
  }

  clearSignature() {
    this.clear()
  }

  clearAdminSignature() {
    if (this.hasExistingDisplayTarget) {
      this.existingDisplayTarget.classList.add("hidden")
    }

    if (this.hasExistingImageTarget) {
      this.existingImageTarget.classList.add("hidden")
      this.existingImageTarget.removeAttribute("src")
    }

    if (this.hasPadSectionTarget) {
      this.padSectionTarget.classList.remove("hidden")
    }

    this.clearStoredSignatureData()
    this.ensureCanvasReady()
    this.clear()
  }

  clearStoredSignatureData() {
    if (this.hasSignatureDataTarget) {
      this.signatureDataTarget.value = ""
    }
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = ""
    }
  }

  updateSignatureData() {
    this.ensureCanvasReady()
    if (!this.canvas) return

    const signatureData = this.canvas.toDataURL("image/png")

    if (this.hasSignatureDataTarget) {
      this.signatureDataTarget.value = signatureData
    }
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = signatureData
    }
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      const isEmpty = this.isEmpty()
      this.submitButtonTarget.disabled = isEmpty

      if (isEmpty) {
        this.submitButtonTarget.classList.add("bg-gray-400", "cursor-not-allowed")
        this.submitButtonTarget.classList.remove("bg-blue-600", "hover:bg-blue-700")
      } else {
        this.submitButtonTarget.classList.remove("bg-gray-400", "cursor-not-allowed")
        this.submitButtonTarget.classList.add("bg-blue-600", "hover:bg-blue-700")
      }
    }
  }

  submitForm(event) {
    if (this.isEmpty()) {
      alert("Please provide a signature before submitting.")
      return false
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.innerHTML = "Submitting..."
    }

    this.updateSignatureData()

    if (this.hasFormTarget) {
      this.formTarget.submit()
      return true
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.innerHTML = "Add Admin Signature"
    }
    return false
  }

  isEmpty() {
    this.ensureCanvasReady()
    if (!this.ctx || !this.canvas) return true

    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height)
    const data = imageData.data

    for (let i = 0; i < data.length; i += 4) {
      if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
        return false
      }
    }
    return true
  }

  getSignatureData() {
    return this.canvas.toDataURL("image/png")
  }
}
